import { ErrorCode } from '@ccex/shared';
import type { ApiError } from '@/api/http';

export type Classification =
  | {
      kind: 'event';
      level: 'error' | 'warning';
      reason: 'server' | 'bad_response' | 'non_problem' | 'network' | 'unknown_code';
      /** Sentry grouping key: one issue per endpoint and code, not per user or per id. */
      fingerprint: string[];
    }
  | { kind: 'breadcrumb' };

/** Codes the API can answer with on purpose (spec 2026-10-08 §2: business refusals are not errors). */
const KNOWN_CODES: ReadonlySet<string> = new Set(Object.values(ErrorCode));

export function classifyApiError(
  error: ApiError,
  at: { method: string; path: string },
): Classification {
  const byEndpoint = [at.method, at.path, error.code];
  if (error.code === 'NETWORK')
    return {
      kind: 'event',
      level: 'warning',
      reason: 'network',
      fingerprint: ['network', at.method, at.path],
    };
  // Non-problem answers (e.g. a Cloudflare 52x page) carry a 5xx status but are not API errors.
  if (error.code.startsWith('HTTP_'))
    return { kind: 'event', level: 'error', reason: 'non_problem', fingerprint: byEndpoint };
  if (error.code === 'BAD_RESPONSE')
    return { kind: 'event', level: 'error', reason: 'bad_response', fingerprint: byEndpoint };
  if (error.status >= 500)
    return { kind: 'event', level: 'error', reason: 'server', fingerprint: byEndpoint };
  if (!KNOWN_CODES.has(error.code))
    return { kind: 'event', level: 'warning', reason: 'unknown_code', fingerprint: byEndpoint };
  return { kind: 'breadcrumb' };
}
